import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  DatePicker,
  Form,
  Input,
  Select,
  Switch,
  message,
  Upload,
  Modal,
  Tag,
  Spin
} from "antd";
import { InboxOutlined, CloseOutlined } from "@ant-design/icons";

import moment from "moment";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode"; // Fix the import
import { useDispatch, useSelector } from "react-redux";
import Cookies from "universal-cookie";
import { createFundraiser } from "../../../../Services/services/Fundraisers";
import debounce from "lodash/debounce";

const { Option } = Select;
const { useForm } = Form;

const CreateFundraiser = () => {
  const isMenuOpen = useSelector((state) => state.sidebar.isMenuOpen);
  const [documents, setDocuments] = useState([]);
  const [authorizedDocuments, setAuthorizedDocuments] = useState([]);
  const [coverImageloading, setCoverImageloading] = useState(false);
  const [form] = useForm();
  const today = moment();
  const [causes, setCauses] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [isIndividual, setIsIndividual] = useState(false);
  const [isRoleChecked, setIsRoleChecked] = useState(false); // New state to check role
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [campaignCovers, setCampaignCovers] = useState([]);
  const [deletedCampaignCovers, setDeletedCampaignCovers] = useState([]);
  const dispatch = useDispatch();
  const cookies = new Cookies();
  const token = cookies.get("jwtToken");
  const navigate = useNavigate();
  const [impactForm] = useForm();
  const [open, setOpen] = useState(false);
  const [impacts, setImpact] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const addImpact = (newImpact) => {
    setImpact((prevImpacts) => [...prevImpacts, newImpact]);
  };

  const handleOk = () => {
    impactForm
      .validateFields()
      .then((values) => {
        console.log("Impact Form Values:", values); // Debug log

        setImpact((prevImpacts) => [...prevImpacts, values]);
        message.success("Impact added successfully!");
        impactForm.resetFields();
        setOpen(false);
      })
      .catch((info) => {
        console.error("Validation Error:", info); // Debug log
        message.error("Please fill in the required fields.");
      });
  };

  useEffect(() => {
    const fetchCauses = async () => {
      try {
        const response = await axios.get(
          `${import.meta.env.VITE_API_URL_DASHBOARD_URL}/cause`
        );
        if (response.data && Array.isArray(response.data.causes)) {
          setCauses(response.data.causes);
        } else {
          throw new Error("API response is not an array");
        }
      } catch (error) {
        message.error(error.response.data.error);
      }
    };

    const fetchCampaigns = async () => {
      try {
        const response = await axios.get(
          `${
            import.meta.env.VITE_API_URL_DASHBOARD_URL
          }/campaign?sort_title=name&sort_value=1`
        );
        if (response.data && Array.isArray(response.data.docs)) {
          setCampaigns(response.data.docs);
        } else {
          throw new Error("API response is not an array");
        }
      } catch (error) {
        message.error(error.response.data.error);
      }
    };

    fetchCauses();
    fetchCampaigns();
  }, []);

  useEffect(() => {
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        console.log("Decoded token role:", decodedToken.role); // Debug
        setIsIndividual(decodedToken.role === "individual");
      } catch (error) {
        console.error("Failed to decode token:", error);
      } finally {
        setIsRoleChecked(true); // Mark role check as complete
      }
    } else {
      console.warn("No token found in cookies");
      setIsRoleChecked(true); // Mark role check as complete even if no token
    }
  }, [token]);

  const handleUserSearch = async (value) => {
    if (!value) {
      setUsers([]);
      return;
    }

    try {
      const response = await axios.get(
        `${
          import.meta.env.VITE_API_URL_DASHBOARD_URL
        }/user/search?text=${value}`
      );
      if (response.data && Array.isArray(response.data.matches)) {
        console.log("API Response:", response.data.matches); // Debug log
        setUsers(response.data.matches);
      } else {
        throw new Error("API response is not an array");
      }
    } catch (error) {
      message.error(error.response.data.error);
    }
  };

  const debouncedHandleUserSearch = useCallback(
    debounce(handleUserSearch, 1000),
    []
  );

  const handleNewCauseOk = async () => {
    try {
      const values = await newCauseForm.validateFields();
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL_DASHBOARD_URL}/cause`,
        values
      );
      setCauses([...causes, response.data]);
      newCauseForm.resetFields();
      setNewCauseOpen(false);
      message.success("New cause added successfully!");
    } catch (error) {
      message.error("Failed to add new cause.");
    }
  };

  const uploadDocument = async (
    file,
    setLoading,
    setUploadSuccess,
    setMessage,
    setKey
  ) => {
    setLoading(true);
    const token = cookies.get("jwtToken");
    const filename = file.name;

    try {
      const uploadResponse = await axios.post(
        `${import.meta.env.VITE_API_URL}/upload/open_file_upload`,
        { filename: filename.replace(/\s+/g, "_") },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const { url, key } = uploadResponse.data;

      const s3UploadResponse = await fetch(url, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      });

      if (s3UploadResponse.ok) {
        // message.success("Document uploaded successfully!");
        setUploadSuccess(true);
        setMessage(filename);
        // setKeyF(key); // This can stay if you still need to update the state
        return uploadResponse.data.key; // Return the key
      } else {
        throw new Error("Failed to upload file to S3");
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      message.error("An error occurred while uploading the document.");
      setUploadSuccess(false);
    }
    setLoading(false);
  };

  const handleDocumentUpload = async ({ file, onSuccess, onError }) => {
    setLoading(true);
    try {
      const key = await uploadDocument(
        file,
        setCoverImageloading,
        (success) => {
          if (!success) {
            onError(new Error("Failed to upload document"));
          }
        },
        (filename) => message.success(`${filename} uploaded successfully!`)
      );

      if (key) {
        setDocuments((prevDocs) => [
          ...prevDocs,
          {
            uid: file.uid,
            url: `${import.meta.env.VITE_API_CLOUDFRONT_URL}${key}`,
            name: file.name,
          },
        ]);

        onSuccess(null, file);
      } else {
        onError(new Error("Failed to upload document: Key not received"));
      }
    } catch (error) {
      console.error("Error during document upload:", error);
      message.error("An error occurred while uploading the document.");
      onError(error);
    } finally {
      setCoverImageloading(false);
    }
  }

  const handleRemoveImage = (uid) => {
    setDocuments((prevDocs) => prevDocs.filter((doc) => doc.uid !== uid));
  };

  const handleRemoveAuthorizedImage = (uid) => {
    setAuthorizedDocuments((prevDocs) =>
      prevDocs.filter((doc) => doc.uid !== uid)
    );
  };

  const handleRemoveCampaignCover = (url) => {
    setCampaignCovers((prevCovers) =>
      prevCovers.filter((cover) => cover !== url)
    );
    setDeletedCampaignCovers((prevDeleted) => [...prevDeleted, url]);
  };

  const handleUrlKeyPress = (event) => {
    if (event.key === " ") {
      event.preventDefault();
      const { value } = event.target;
      event.target.value = value + "-";
    }
  };

  const handleCampaignChange = (campaigns, campaignId) => {
    const selectedCampaign = campaigns.find(
      (campaign) => campaign._id === campaignId
    );
    if (selectedCampaign) {
      setCampaignCovers(selectedCampaign.cover || []);
    }
  };

  const onFinish = async (values) => {
    if (!selectedUserId && !isIndividual) {
      message.error("Please select a user");
      return;
    }

    const goal = parseFloat(values.goal);
    const fundraiserData = {
      name: values.name,
      goal: goal,
      // currency: "INR",
      // cause: values.cause,
      campaign: values.campaign,
      dates: {
        start_date: values.startDate
          ? values.startDate.toISOString()
          : today.toISOString(),
        last_date: values.endDate ? values.endDate.toISOString() : null,
      },
      status: values.isActive ? "active" : "draft",
      cover: [
        ...campaignCovers.filter((url) => !deletedCampaignCovers.includes(url)),
        ...documents.map((doc) => doc.url),
      ],
      slug: values.url,
      // documents: authorizedDocuments.map((doc) => doc.url),
      impacts: impacts,
    };

    try {
      if (fundraiserData.cover.length === 0) {
        message.error("Please upload cover image first");
      } else {
        console.log("Fundraiser Data:", fundraiserData);
        const response = await createFundraiser(fundraiserData, selectedUserId);
        console.log("Response from createFundraiser:", response);
        message.success("Fundraiser created successfully!");
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Error in onFinish:", error);
      message.error(error.response.data.error);
    }
  };

  const disablePastDates = (current) => {
    return current && current < moment().startOf("day");
  };
  const showModal = () => {
    setOpen(true);
  };
  const handleCancel = () => {
    setOpen(false);
  };
  // console.log(sidebarOpen)

  return (
    <div
      className={`bg-[#41c0e0] sm:p-4  h-full min-h-[100vh] p-8  ${
        isMenuOpen ? "ml-64" : "ml-10"
      } transition-all duration-300 `}
    >
      <div className="max-w-4xl mx-auto bg-white p-8 rounded-lg shadow">
        <div className="flex">
          <h1 className="text-xl font-bold mb-6 flex-grow">
            Create Fundraiser
          </h1>
        </div>
        <Button
          type="primary"
          className="ml-[50vw] bg-[#2d98b3]"
          onClick={showModal}
        >
          Add Impact
        </Button>
        <Modal
          open={open}
          title="Add Impact"
          onOk={handleOk}
          onCancel={handleCancel}
          className="mt-16"
          footer={[
            <Button key="back" onClick={handleCancel}>
              Cancel
            </Button>,
            <Button key="submit" type="primary" onClick={handleOk}>
              Submit
            </Button>,
          ]}
        >
          <Form
            form={impactForm}
            labelCol={{ span: 4 }}
            wrapperCol={{ span: 20 }}
          >
            <Form.Item
              label="Title"
              name="title"
              rules={[{ required: true, message: "Please enter title" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Description"
              name="description"
              rules={[{ required: true, message: "Please enter description" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Count"
              name="count"
              rules={[{ required: true, message: "Please enter count" }]}
            >
              <Input />
            </Form.Item>
          </Form>
        </Modal>

        {/* Ensure the form renders only after role check is complete */}
        {isRoleChecked && (
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{ isActive: true }}
          >
            <Form.Item
              label="Choose Campaign"
              name="campaign"
              rules={[{ required: true, message: "Please select a campaign" }]}
            >
              <Select
                placeholder="Select a campaign"
                onChange={handleCampaignChange}
              >
                {campaigns.map((campaign) => (
                  <Option key={campaign._id} value={campaign._id}>
                    {campaign.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="Cover Image">
              <Spin spinning={coverImageloading}>
                <Upload.Dragger
                  name="files"
                  customRequest={handleDocumentUpload}
                  multiple={false}
                  accept=".jpg,.jpeg,.png"
                  showUploadList={false}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    Click or drag file to this area to upload
                  </p>
                  <p className="ant-upload-hint">Upload campaign cover image</p>
                </Upload.Dragger>
              </Spin>
            </Form.Item>

            <div className="image-preview-grid mt-4 bg-gray-100 mb-4 rounded-lg min-h-0">
              {campaignCovers.map((url) => (
                <div key={url} className="relative w-40 h-40 mb-2 mt-2">
                  <img
                    src={url}
                    alt="Campaign Cover"
                    className="w-full h-full object-cover rounded-lg"
                  />
                  <Button
                    type="text"
                    className="absolute top-1 right-1 text-white bg-gray-800 rounded-full p-1"
                    onClick={() => handleRemoveCampaignCover(url)}
                    icon={<CloseOutlined />}
                  />
                </div>
              ))}
              {documents.map((doc) => (
                <div key={doc.uid} className="relative w-40 h-40 mb-2 mt-2">
                  <img
                    src={doc.url}
                    alt="Document"
                    className="w-full h-full object-cover rounded-lg"
                  />
                  <Button
                    type="text"
                    className="absolute top-1 right-1 text-white bg-gray-800 rounded-full p-1"
                    onClick={() => handleRemoveImage(doc.uid)}
                    icon={<CloseOutlined />}
                  />
                </div>
              ))}
            </div>

            {/* <Upload.Dragger
              accept="image/*"
              customRequest={handleAuthorizedDocumentUpload}
              showUploadList={false}
              multiple
              className="mt-4"
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag file to this area to upload Authorized Documents
              </p>
            </Upload.Dragger> */}

            <div className="image-preview-grid mt-4 bg-gray-100 mb-4 rounded-lg min-h-0">
              {authorizedDocuments.map((doc) => (
                <div key={doc.uid} className="relative w-40 h-40 mb-2 mt-2">
                  <img
                    src={doc.url}
                    alt="Authorized Document"
                    className="w-full h-full object-cover rounded-lg"
                  />
                  <Button
                    type="text"
                    className="absolute top-1 right-1 text-white bg-gray-800 rounded-full p-1"
                    onClick={() => handleRemoveAuthorizedImage(doc.uid)}
                    icon={<CloseOutlined />}
                  />
                </div>
              ))}
            </div>

            <Form.Item
              label="Name"
              name="name"
              rules={[
                { required: true, message: "Please enter the fundraiser name" },
              ]}
            >
              <Input
                placeholder="Enter the name of the fundraiser"
                className="input-style"
              />
            </Form.Item>

            <Form.Item
              label="Goal"
              name="goal"
              
              rules={[
                { required: true, message: "Please enter the goal amount" },
              ]}
            >
              <Input
                placeholder="Enter the goal amount"
                type="number"
                className="input-style"
              />
            </Form.Item>
            {/* <Form.Item
              label="Cause"
              name="cause"
              rules={[{ required: true, message: "Please select a cause" }]}
            >
              <Select placeholder="Select a cause">
                {causes.map((cause) => (
                  <Option key={cause._id} value={cause._id}>
                    {cause.name}
                  </Option>
                ))}
              </Select>
            </Form.Item> */}
            {/* <div className="grid grid-cols-2 gap-4">
              <Form.Item label="Start Date" name="startDate">
                <DatePicker
                  format="YYYY-MM-DD"
                  disabledDate={disablePastDates}
                  className="w-full"
                />
              </Form.Item>

              <Form.Item label="End Date" name="endDate">
                <DatePicker
                  format="YYYY-MM-DD"
                  disabledDate={disablePastDates}
                  className="w-full"
                />
              </Form.Item>
            </div> */}
            <Form.Item label="URL" name="url">
              <Input
                placeholder="Enter the URL of the fundraiser"
                onKeyPress={handleUrlKeyPress}
                className="input-style"
              />
            </Form.Item>

            <Form.Item
              label="Is Active?"
              name="isActive"
              valuePropName="checked"
            >
              <Switch defaultChecked />
            </Form.Item>
            {impacts.length > 0 && (
              <div
                className="mb-4"
                style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}
              >
                {impacts.map((impact, index) => (
                  <Tag color="magenta" key={index}>
                    <div>{impact.title}</div>
                    <div>{impact.amount}</div>
                  </Tag>
                ))}
              </div>
            )}

            <Form.Item
              label="Select User"
              name="user"
              rules={[{ required: true, message: "Please select a user" }]}
            >
              <Select
                showSearch
                placeholder="Search for a user"
                onSearch={debouncedHandleUserSearch}
                onChange={(value) => setSelectedUserId(value)}
                filterOption={false}
              >
                {users.map((user) => (
                  <Option key={user._id} value={user._id}>
                    {user.email}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                className="w-4/12 bg-[#2d98b3] left-1/4 p-5"
              >
                Create Fundraiser
              </Button>
            </Form.Item>
          </Form>
        )}
      </div>
    </div>
  );
};

export default CreateFundraiser;
